import React, { useState } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import Modal from "aldoo-ra/Modal"
import { showNotification } from "aldoo-ra/Notification"
import {
  MagnifyingGlassIcon,
  UserMinusIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline"
import { SearchUsers, AddUserToLicense } from "../../api"

const AddUserDialog = ({
  isOpen,
  onClose,
  license,
  refreshKey,
  setRefreshKey
}) => {
  const [userSearch, setUserSearch] = useState("")
  const [userSearchResults, setUserSearchResults] = useState([])
  const [loadingUsers, setLoadingUsers] = useState(false)

  // Search users function
  const searchUsers = async (searchTerm) => {
    if (!searchTerm.trim()) {
      setUserSearchResults([])
      return
    }

    try {
      setLoadingUsers(true)
      const response = await SearchUsers({
        search: searchTerm,
        limit: 15
      })

      if (response.result && response.data) {
        // Create a set of existing user IDs for quick lookup
        const existingUserIds = new Set(
          (license?.users || []).map(u => u._id).filter(Boolean)
        )

        // Mark users as already added and include all results
        const usersWithStatus = response.data.map(user => ({
          ...user,
          isAlreadyAdded: existingUserIds.has(user._id),
          isOwner: license?.owner?._id === user._id
        }))

        setUserSearchResults(usersWithStatus)
      } else {
        setUserSearchResults([])
      }
    } catch (error) {
      setUserSearchResults([])
    } finally {
      setLoadingUsers(false)
    }
  }

  // Handle adding user to license
  const handleAddUser = async (user) => {
    try {
      const response = await AddUserToLicense({
        licenseId: license._id,
        userId: user._id,
        makePrimary: false // Explicitly set to false to ensure secondary user addition
      })

      if (response.result) {
        showNotification({
          message: `${user.name || user.email} added to license as secondary user`,
          className: "bg-admin_primary text-admin_text_inv",
        })
        setRefreshKey(prev => prev + 1)
        // Close dialog after successful addition
        handleClose()
      } else {
        showNotification({
          message: response.message || response.error || "Failed to add user",
          className: "bg-admin_error text-white",
        })
      }
    } catch (error) {
      // Extract error message from different possible sources
      let errorMessage = "An error occurred while adding user"

      if (error.response && error.response.data) {
        const responseData = error.response.data
        errorMessage = responseData.message || responseData.error || error.message
      } else if (error.message) {
        errorMessage = error.message
      }

      showNotification({
        message: errorMessage,
        className: "bg-admin_error text-white",
      })
    }
  }

  const handleClose = () => {
    onClose()
    setUserSearch("")
    setUserSearchResults([])
  }

  return (
    <Modal
      variant="admin"
      isOpen={isOpen}
      onClose={handleClose}
      title="Add User to License"
      className="max-w-2xl h-[85vh] !overflow-hidden"
      footerContent={
        <div className="space-y-3">
          <Typography variant="admin_body_accent" className="text-admin_text">
            User Permissions
          </Typography>
          <div className="space-y-1">
            <Typography variant="admin_txt2" className="text-admin_text_secondary">
              • Secondary users can activate devices using the license code
            </Typography>
            <Typography variant="admin_txt2" className="text-admin_text_secondary">
              • They cannot add or remove other users from the license
            </Typography>
            <Typography variant="admin_txt2" className="text-admin_text_secondary">
              • Device activations count towards the total seat limit ({license.seats} seats)
            </Typography>
          </div>
        </div>
      }
    >
      <div className="h-full flex flex-col space-y-4">
        <Typography variant="admin_txt1" className="text-admin_text_secondary">
          Search for users to add as secondary users to this license. Secondary users can activate devices but cannot manage other users.
        </Typography>

        {/* Search Input */}
        <div className="relative flex-shrink-0">
          <Input
            variant="admin"
            placeholder="Search users by name or email..."
            value={userSearch}
            onChange={(e) => {
              setUserSearch(e.target.value)
              searchUsers(e.target.value)
            }}
            className="w-full"
            autoFocus
          />
          <MagnifyingGlassIcon className="absolute right-3 top-3 w-5 h-5 text-admin_text_secondary" />
        </div>

        {/* Search Results */}
        <div className="flex-1 overflow-hidden">
          {loadingUsers && (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-admin_primary mx-auto"></div>
              <Typography variant="admin_txt1" className="text-admin_text_secondary mt-3">
                Searching users...
              </Typography>
            </div>
          )}

          {!loadingUsers && userSearchResults.length > 0 && (
            <div className="space-y-2 overflow-y-auto h-full">
              <Typography variant="admin_txt2" className="text-admin_text_secondary mb-3">
                Found {userSearchResults.length} user{userSearchResults.length === 1 ? "" : "s"}
              </Typography>
              {userSearchResults.map((user) => (
                <div key={user._id} className={`flex items-center justify-between p-4 rounded-lg transition-colors ${
                  user.isAlreadyAdded
                    ? 'bg-admin_neutral_1/30 border border-admin_neutral_1'
                    : 'bg-admin_canvas hover:bg-admin_neutral_1/50'
                }`}>
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      user.isOwner
                        ? 'bg-admin_primary'
                        : user.isAlreadyAdded
                          ? 'bg-admin_neutral_1'
                          : 'bg-admin_text_secondary'
                    }`}>
                      <Typography variant="admin_txt1" className="text-white font-medium">
                        {(user.name || user.email).charAt(0).toUpperCase()}
                      </Typography>
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <Typography variant="admin_body" className={`font-medium ${user.isAlreadyAdded ? 'text-admin_text_secondary' : ''}`}>
                          {user.name || user.email}
                        </Typography>
                        {user.isOwner && (
                          <span className="px-2 py-1 bg-admin_primary text-white text-xs rounded-full">
                            Primary Owner
                          </span>
                        )}
                        {user.isAlreadyAdded && !user.isOwner && (
                          <span className="px-2 py-1 bg-admin_text_secondary text-white text-xs rounded-full">
                            Secondary User
                          </span>
                        )}
                      </div>
                      {user.name && (
                        <Typography variant="admin_txt2" className="text-admin_text_secondary">
                          {user.email}
                        </Typography>
                      )}
                    </div>
                  </div>
                  <div>
                    {!user.isAlreadyAdded ? (
                      <Button
                        variant="admin_primary"
                        onClick={() => handleAddUser(user)}
                      >
                        <UserPlusIcon className="w-4 h-4 mr-2" />
                        Add User
                      </Button>
                    ) : (
                      <div className="text-right">
                        <Typography variant="admin_txt2" className="text-admin_success font-medium">
                          {user.isOwner ? 'License Owner' : 'Already Added'}
                        </Typography>
                        <Typography variant="admin_txt2" className="text-admin_text_secondary text-xs">
                          {user.isOwner ? 'Has full control' : 'Can use license'}
                        </Typography>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loadingUsers && userSearch && userSearchResults.length === 0 && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <UserMinusIcon className="w-12 h-12 text-admin_text_secondary mx-auto mb-4" />
                <Typography variant="admin_body" className="text-admin_text_secondary mb-2">
                  No users found
                </Typography>
                <Typography variant="admin_txt2" className="text-admin_text_secondary">
                  No users found for "{userSearch}". Try a different search term.
                </Typography>
              </div>
            </div>
          )}

          {!userSearch && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <MagnifyingGlassIcon className="w-12 h-12 text-admin_text_secondary mx-auto mb-4" />
                <Typography variant="admin_body" className="text-admin_text_secondary mb-2">
                  Search for users
                </Typography>
                <Typography variant="admin_txt2" className="text-admin_text_secondary">
                  Start typing a name or email address to search for users.
                </Typography>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default AddUserDialog